import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { SqlImportFormValues } from '@/features/sqlImport/types'

const validateFile = (file: File | null, formValues: SqlImportFormValues) => {
	if (formValues.inputMode !== 'file') {
		return true
	}

	if (!file) {
		return 'Please select a file'
	}

	if (!file.name.toLowerCase().endsWith('.sql')) {
		return 'Please select a .sql file'
	}

	if (file.size === 0) {
		return 'The selected file is empty'
	}

	const maxSizeBytes = 10 * 1024 * 1024
	if (file.size > maxSizeBytes) {
		return 'File size must not exceed 10MB'
	}

	return true
}

const validateSqlText = (text: string, formValues: SqlImportFormValues) => {
	if (formValues.inputMode !== 'paste') {
		return true
	}

	const trimmedText = text.trim()
	if (!trimmedText) {
		return 'Please enter SQL code or select a file'
	}

	return true
}

export const useImportForm = () => {
	const {
		control,
		handleSubmit,
		watch,
		reset: resetForm,
		setValue,
		register,
		trigger,
		formState: { errors },
	} = useForm<SqlImportFormValues>({
		defaultValues: {
			inputMode: 'file',
			selectedFile: null,
			sqlText: '',
		},
	})

	register('selectedFile', {
		validate: (file, formValues) =>
			validateFile(file, formValues as SqlImportFormValues),
	})

	register('sqlText', {
		validate: (text, formValues) =>
			validateSqlText(text, formValues as SqlImportFormValues),
	})

	const inputMode = watch('inputMode')
	const selectedFile = watch('selectedFile')
	const sqlText = watch('sqlText')

	useEffect(() => {
		setValue('selectedFile', null)
		setValue('sqlText', '')
		setValue('selectedFile', null, { shouldValidate: true })
		setValue('sqlText', '', { shouldValidate: true })
	}, [inputMode, setValue])

	const reset = () => {
		resetForm({
			inputMode: 'file',
			selectedFile: null,
			sqlText: '',
		})
	}

	return {
		control,
		handleSubmit,
		inputMode,
		selectedFile,
		sqlText,
		errors,
		setValue,
		trigger,
		reset,
	}
}

